// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * First-party monophonic pitch detection — the McLeod Pitch Method (MPM).
 *
 * We compute the Normalised Square Difference Function (NSDF) of a windowed
 * frame and pick its fundamental peak. The autocorrelation term is evaluated
 * with an FFT (O(N log N)) so this stays cheap enough to run ~20×/second on a
 * phone; the normalisation term uses running sums of squares. No libraries.
 *
 * Everything here is a pure function of its inputs, which is what lets the
 * automation — unable to whistle into a microphone — test the maths exhaustively
 * with synthesised tones.
 */

// ── Radix-2 FFT (iterative, in-place) ───────────────────────────────────────

/** In-place radix-2 FFT. `re`/`im` length must be a power of two. */
export function fft(re: Float64Array, im: Float64Array, inverse = false): void {
  const n = re.length;
  if (n <= 1) return;
  // Bit-reversal permutation.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i];
      re[i] = re[j];
      re[j] = tr;
      const ti = im[i];
      im[i] = im[j];
      im[j] = ti;
    }
  }
  const sign = inverse ? 1 : -1;
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (sign * 2 * Math.PI) / len;
    const wRe = Math.cos(ang);
    const wIm = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let curRe = 1;
      let curIm = 0;
      const half = len >> 1;
      for (let k = 0; k < half; k++) {
        const aRe = re[i + k];
        const aIm = im[i + k];
        const bRe = re[i + k + half];
        const bIm = im[i + k + half];
        const tRe = bRe * curRe - bIm * curIm;
        const tIm = bRe * curIm + bIm * curRe;
        re[i + k] = aRe + tRe;
        im[i + k] = aIm + tIm;
        re[i + k + half] = aRe - tRe;
        im[i + k + half] = aIm - tIm;
        const nextRe = curRe * wRe - curIm * wIm;
        curIm = curRe * wIm + curIm * wRe;
        curRe = nextRe;
      }
    }
  }
  if (inverse) {
    for (let i = 0; i < n; i++) {
      re[i] /= n;
      im[i] /= n;
    }
  }
}

function nextPow2(n: number): number {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

// ── NSDF via FFT autocorrelation ─────────────────────────────────────────────

/**
 * Autocorrelation r(τ) for τ = 0..maxLag using zero-padded FFT (avoids circular
 * wraparound). Returns a Float64Array of length maxLag+1.
 */
export function autocorrelate(x: Float32Array, maxLag: number): Float64Array {
  const w = x.length;
  const fftLen = nextPow2(w + maxLag + 1);
  const re = new Float64Array(fftLen);
  const im = new Float64Array(fftLen);
  for (let i = 0; i < w; i++) re[i] = x[i];
  fft(re, im, false);
  for (let i = 0; i < fftLen; i++) {
    re[i] = re[i] * re[i] + im[i] * im[i];
    im[i] = 0;
  }
  fft(re, im, true);
  const out = new Float64Array(maxLag + 1);
  for (let tau = 0; tau <= maxLag; tau++) out[tau] = re[tau];
  return out;
}

export interface PitchResult {
  /** Fundamental frequency in Hz, or -1 when no confident pitch was found. */
  frequency: number;
  /** Peak NSDF value at the chosen lag (0..1) — the clarity/confidence. */
  clarity: number;
}

export interface PitchOptions {
  /** Lowest frequency to look for (Hz). Default 40 (below bass low-E). */
  minHz?: number;
  /** Highest frequency to look for (Hz). Default 1500. */
  maxHz?: number;
  /** NSDF value below which we report "no pitch". Default 0.6. */
  clarityThreshold?: number;
  /** Fraction of the max NSDF peak used to pick the fundamental. Default 0.9. */
  pickThreshold?: number;
}

/**
 * Detect the fundamental frequency of a windowed audio frame.
 * Returns `{ frequency: -1 }` when the frame is too quiet or aperiodic.
 */
export function detectPitch(frame: Float32Array, sampleRate: number, opts: PitchOptions = {}): PitchResult {
  const minHz = opts.minHz ?? 40;
  const maxHz = opts.maxHz ?? 1500;
  const clarityThreshold = opts.clarityThreshold ?? 0.6;
  const pickThreshold = opts.pickThreshold ?? 0.9;

  const w = frame.length;
  // Reject near-silence: RMS below ~ -55 dBFS carries no useful pitch.
  let energy = 0;
  for (let i = 0; i < w; i++) energy += frame[i] * frame[i];
  const rms = Math.sqrt(energy / w);
  if (rms < 0.0018) return { frequency: -1, clarity: 0 };

  const minLag = Math.max(2, Math.floor(sampleRate / maxHz));
  const maxLag = Math.min(w - 2, Math.ceil(sampleRate / minHz));
  if (maxLag <= minLag) return { frequency: -1, clarity: 0 };

  const r = autocorrelate(frame, maxLag);

  // Running sum of squares → the NSDF normalisation term m(τ).
  // prefix[k] = Σ_{j<k} x[j]²
  const prefix = new Float64Array(w + 1);
  for (let i = 0; i < w; i++) prefix[i + 1] = prefix[i] + frame[i] * frame[i];
  const total = prefix[w];

  const nsdf = new Float64Array(maxLag + 1);
  for (let tau = 0; tau <= maxLag; tau++) {
    // m(τ) = Σ_{j=0}^{w-1-τ} x[j]²  +  Σ_{j=τ}^{w-1} x[j]²
    const m = prefix[w - tau] + (total - prefix[tau]);
    nsdf[tau] = m > 0 ? (2 * r[tau]) / m : 0;
  }

  // Collect "key maxima": the highest local maximum in each positive lobe,
  // starting after the NSDF first dips below zero (skips the τ=0 lobe).
  let tau = minLag;
  // advance past the initial positive lobe
  while (tau <= maxLag && nsdf[tau] > 0) tau++;

  const maxima: number[] = [];
  while (tau <= maxLag) {
    if (nsdf[tau] > 0) {
      let bestIdx = tau;
      let bestVal = nsdf[tau];
      while (tau <= maxLag && nsdf[tau] > 0) {
        if (nsdf[tau] > bestVal) {
          bestVal = nsdf[tau];
          bestIdx = tau;
        }
        tau++;
      }
      if (bestIdx >= minLag) maxima.push(bestIdx);
    } else {
      tau++;
    }
  }

  if (maxima.length === 0) return { frequency: -1, clarity: 0 };

  let globalMax = 0;
  for (const idx of maxima) if (nsdf[idx] > globalMax) globalMax = nsdf[idx];
  if (globalMax < clarityThreshold) return { frequency: -1, clarity: globalMax };

  const threshold = pickThreshold * globalMax;
  let chosen = maxima[0];
  for (const idx of maxima) {
    if (nsdf[idx] >= threshold) {
      chosen = idx;
      break;
    }
  }

  // Parabolic interpolation around the chosen lag for sub-sample accuracy.
  const { position, value } = parabolic(nsdf, chosen);
  const frequency = sampleRate / position;
  if (frequency < minHz || frequency > maxHz) return { frequency: -1, clarity: value };
  return { frequency, clarity: Math.max(0, Math.min(1, value)) };
}

/** Parabolic peak interpolation around index `i` of array `a`. */
export function parabolic(a: Float64Array, i: number): { position: number; value: number } {
  if (i <= 0 || i >= a.length - 1) return { position: i, value: a[i] };
  const x0 = a[i - 1];
  const x1 = a[i];
  const x2 = a[i + 1];
  const denom = x0 - 2 * x1 + x2;
  if (denom === 0) return { position: i, value: x1 };
  const delta = (0.5 * (x0 - x2)) / denom;
  return { position: i + delta, value: x1 - 0.25 * (x0 - x2) * delta };
}
